import { useEffect, useState, type ReactNode } from 'react'

interface FullscreenIconButtonProps {
  expanded: boolean
  onToggle: () => void
}

export function FullscreenIconButton({ expanded, onToggle }: FullscreenIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={expanded ? 'Tam ekrandan çık' : 'Tam ekran'}
      title={expanded ? 'Tam ekrandan çık (Esc)' : 'Tam ekran'}
      className="absolute top-1.5 right-1.5 z-20 p-1.5 rounded bg-black/50 hover:bg-black/70 backdrop-blur-sm text-white/85 hover:text-white"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
        {expanded ? (
          <>
            <polyline points="9 3 9 9 3 9" />
            <polyline points="15 21 15 15 21 15" />
            <polyline points="21 9 15 9 15 3" />
            <polyline points="3 15 9 15 9 21" />
          </>
        ) : (
          <>
            <polyline points="3 9 3 3 9 3" />
            <polyline points="21 9 21 3 15 3" />
            <polyline points="3 15 3 21 9 21" />
            <polyline points="21 15 21 21 15 21" />
          </>
        )}
      </svg>
    </button>
  )
}

interface FullscreenShellProps {
  expanded: boolean
  onClose: () => void
  // Class applied while in normal (non-fullscreen) layout. Lets the caller
  // preserve aspect ratios etc.
  inlineClassName?: string
  children: ReactNode
  // Optional title shown at the top while expanded.
  title?: string
}

export function FullscreenShell({
  expanded,
  onClose,
  inlineClassName,
  children,
  title,
}: FullscreenShellProps) {
  useEffect(() => {
    if (!expanded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [expanded, onClose])

  // One stable DOM tree: only CSS classes flip when expanded toggles. This
  // keeps the <canvas> nodes inside `children` mounted, so their bitmap data
  // (width/height + putImageData) survives the fullscreen toggle without a
  // re-render. Conditional siblings get explicit keys for the same reason.
  const outerCls = expanded
    ? 'fixed inset-0 z-50 bg-bg/95 flex flex-col'
    : inlineClassName ?? ''
  const contentCls = expanded ? 'flex-1 min-h-0 overflow-auto p-3 sm:p-4' : ''
  return (
    <div className={outerCls}>
      {expanded && (
        <div
          key="fs-header"
          className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-border/40"
        >
          <div className="text-text-dim text-xs sm:text-sm truncate">{title ?? ''}</div>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2.5 py-1 rounded border border-border bg-surface-2 text-text hover:bg-surface"
          >
            Kapat (Esc)
          </button>
        </div>
      )}
      <div key="fs-content" className={contentCls}>
        {children}
      </div>
    </div>
  )
}

export function useFullscreen(): [boolean, () => void, () => void, () => void] {
  const [expanded, setExpanded] = useState(false)
  return [
    expanded,
    () => setExpanded(true),
    () => setExpanded(false),
    () => setExpanded((v) => !v),
  ]
}
